import React, { useCallback, useEffect, useRef, useState } from 'react'

import cx from 'classnames'
import { useConversationId } from 'conversations/hooks/useConversationId'
import { useOnline } from 'conversations/hooks/useOnline'
import { useTimeoutIf } from 'conversations/hooks/useTimeoutIf'
import {
  conversationLogsSidebarVisible,
  detailsSidebarVisible,
  keyboardShortcutsSidebarVisible,
} from 'conversations/modules/sidebar/selectors'
import { getToastNotification } from 'conversations/modules/toastNotification/selectors'
import { patchAgentAvailability } from 'conversations/modules/user/api'
import { getCurrentUserAvailability, getCurrentUserId } from 'conversations/modules/user/selectors'
import { captureMessage } from 'conversations/utils/logRocket'
import { MS_IN_SECOND } from 'conversations/utils/timeConstants'
import { isDefined } from 'conversations/utils/typescriptHelpers'
import { useDispatch, useSelector } from 'react-redux'
import { CSSTransition } from 'react-transition-group'
import { Dispatch } from 'redux'
import { closeToastNotification } from 'shared/store/actions/toastNotification'
import { track } from 'shared/store/actions/tracking'
import { refreshApplicationState } from 'shared/store/actions/websocket'
import { Availability } from 'shared/typings/user/Availability'

import { Icon } from '@driftt/tide-icons'

import { RootAction } from 'typesafe-actions'

import styles from './styles.module.css'
import fadeAnimationStyles from 'conversations/css/animationStyles/fadeAnimation.module.css'

interface Props {
  'data-ocweb-id': string
  children: React.ReactNode
  onDismiss?: () => void
  className?: string
  isOpen: boolean
}

export const ToastNotification: React.FC<Props> = ({
  children,
  isOpen,
  onDismiss,
  className,
  ...rest
}): JSX.Element | null => {
  return (
    // TODO Move this CSSTransition to it's own component
    <CSSTransition
      in={isOpen}
      appear={true}
      unmountOnExit
      timeout={300}
      classNames={{ ...fadeAnimationStyles }}>
      <div {...rest} className={cx(styles.toastNotification, className)}>
        {children}
        {onDismiss && (
          <div onClick={onDismiss} className={styles.dismissContainer} data-ocweb-id="DismissToast">
            <Icon name={'pb-close'} width={9} height={9} className={styles.whiteDismiss} />
          </div>
        )}
      </div>
    </CSSTransition>
  )
}

const ConnectionOfflineSVG: React.FC = (): JSX.Element => {
  return (
    <svg width="24" height="24" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.53034 26.6224C2.23745 26.3295 2.23745 25.8546 2.53034 25.5617L5.75743 22.3346C4.1167 21.3963 3.00002 19.5621 3.00002 17.4584C3.00002 15.2861 4.19102 13.3457 6.01952 12.4448C6.00677 12.2674 6.00002 12.0917 6.00002 11.9167C6.00002 7.55147 9.36452 4.00006 13.5 4.00006C16.1211 4.00006 19.1603 5.19793 20.6694 7.42267L24.0971 3.99494C24.39 3.70205 24.8649 3.70205 25.1578 3.99494C25.4506 4.28783 25.4506 4.76271 25.1578 5.0556L3.591 26.6224C3.29811 26.9152 2.82323 26.9152 2.53034 26.6224ZM19.5471 8.54491L6.92889 21.1631C5.51103 20.5979 4.50002 19.1504 4.50002 17.4584C4.50002 15.7484 5.53202 14.2411 7.06877 13.7059C7.41902 13.584 7.63352 13.2111 7.57352 12.8263C7.52402 12.5057 7.50002 12.2081 7.50002 11.9167C7.50002 8.42468 10.1918 5.58339 13.5 5.58339C15.7027 5.58339 18.3348 6.61754 19.5471 8.54491ZM23.09 10.815C23.09 11.1031 23.2452 11.3549 23.4765 11.4915L23.4689 11.5C23.8134 11.7831 24.1238 12.1105 24.3922 12.474C25.2241 13.6633 25.4841 15.1396 25.5 15.875C25.5 18.6601 23.5268 21.0208 20.9093 21.3676C20.8666 21.3732 20.8251 21.3825 20.7852 21.3952C20.7169 21.3977 20.6487 21.4021 20.5804 21.4065L20.5802 21.4066L20.5801 21.4066C20.4765 21.4133 20.373 21.42 20.2694 21.42H11.3532C11.1285 21.42 10.9144 21.5157 10.7645 21.6832C10.3091 22.1921 10.6703 23 11.3532 23L20.5 23.0001H20.5C20.5003 23.0001 20.924 23 21.3325 22.9025C24.5802 22.35 27 19.3721 27 15.875C27 13.6485 26.0274 11.6572 24.5072 10.3496C24.3643 10.1558 24.1343 10.03 23.875 10.03C23.4415 10.03 23.09 10.3815 23.09 10.815Z"
        fill="white"
      />
    </svg>
  )
}

const SHOW_SOCKET_DISCONNECT_TOAST_AFTER_MS = 30 * MS_IN_SECOND
const useSocketConnected = (): boolean => {
  const socketConnected = useSelector((store: any) => store.socket?.isConnected() ?? false)
  const [connected, setConnected] = useState(true)
  const prevConnectTime = useRef(0)
  const timeout = useRef<number | null>(null)

  useEffect(() => {
    const currentTime = new Date().getTime()

    if (socketConnected) {
      prevConnectTime.current = currentTime
      setConnected(true)
      if (timeout.current !== null) {
        window.clearTimeout(timeout.current)
      }
    }

    if (!socketConnected) {
      timeout.current = window.setTimeout(() => {
        setConnected(false)
      }, SHOW_SOCKET_DISCONNECT_TOAST_AFTER_MS)
    }
  }, [socketConnected, setConnected])

  return connected
}

const SocketDisconnectedToastNotification: React.FC = (): JSX.Element | null => {
  const isConnected = useSocketConnected()
  const socket = useSelector((store: any) => store.socket)
  const dispatch = useDispatch()
  useEffect(() => {
    if (!isConnected) {
      dispatch(
        track({
          name: 'ShowSocketDisconnectToast',
          properties: {
            socketReconnectTries: socket?.reconnectTimer?.tries,
            socketReadyState: socket?.conn?.readyState,
          },
        })
      )
      // try to manually reconnect socket
      socket &&
        socket.teardown(() => {
          captureMessage('Convo --> Successful teardown of socket. Reconnecting...')
          socket.connect()
          dispatch(refreshApplicationState())
        })
    }
  }, [isConnected, socket, dispatch])

  return (
    <ToastNotification isOpen={!isConnected} data-ocweb-id="SocketDisconnectedToast">
      <div className={styles.connectionOfflineSvgContainer}>
        <ConnectionOfflineSVG />
      </div>
      <div>
        No connection...
        <div
          className={styles.anchorLink}
          onClick={() => window.location.reload()}
          data-ocweb-id="NoConnectionTryAgain">
          Try again
        </div>
      </div>
    </ToastNotification>
  )
}

const OfflineToastNotification: React.FC = (): JSX.Element | null => {
  return (
    <ToastNotification isOpen={true} data-ocweb-id="OfflineToast">
      <div className={styles.connectionOfflineSvgContainer}>
        <ConnectionOfflineSVG />
      </div>
      <div>No connection. Go online to get the latest conversations and messages.</div>
    </ToastNotification>
  )
}

const AwayToastNotification: React.FC = (): JSX.Element | null => {
  const availability = useSelector(getCurrentUserAvailability)
  const currentUserId = useSelector(getCurrentUserId)
  const [isOpen, setOpen] = useState(availability === Availability.OFFLINE)
  const onDismiss = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const setAvailable = useCallback(() => {
    if (currentUserId) {
      setOpen(false)
      patchAgentAvailability(currentUserId, Availability.AVAILABLE)
    }
  }, [currentUserId])

  useEffect(() => {
    setOpen(availability === Availability.OFFLINE)
  }, [availability])

  return (
    <ToastNotification onDismiss={onDismiss} isOpen={isOpen} data-ocweb-id="AwayToast">
      <div>
        <div>You are currently set to Away and are not receiving new conversations</div>
        <div className={styles.anchorLink} onClick={setAvailable} data-ocweb-id="SetAvailable">
          Set yourself to Available
        </div>
      </div>
    </ToastNotification>
  )
}

const ToastNotifications: React.FC = (): JSX.Element => {
  const online = useOnline()
  const conversationId = useConversationId()
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const toast = useSelector(getToastNotification)
  const detailsSidebarExpanded = useSelector(detailsSidebarVisible)
  const conversationLogsSidebarExpanded = useSelector(conversationLogsSidebarVisible)
  const keyboardShortcutsSidebarExpanded = useSelector(keyboardShortcutsSidebarVisible)

  const onDismiss = useCallback(() => {
    dispatch(closeToastNotification())
  }, [dispatch])

  useTimeoutIf(
    () => {
      dispatch(closeToastNotification())
    },
    5000,
    () => isDefined(toast) && !!toast?.open
  )

  return (
    <div
      className={cx(styles.verticalStack, {
        [styles.sidebarExpanded]:
          (detailsSidebarExpanded ||
            conversationLogsSidebarExpanded ||
            keyboardShortcutsSidebarExpanded) &&
          conversationId,
      })}>
      {online && <SocketDisconnectedToastNotification />}
      {online ? <AwayToastNotification /> : <OfflineToastNotification />}
      <ToastNotification
        onDismiss={onDismiss}
        isOpen={toast ? toast.open : false}
        data-ocweb-id={'ConversationSectionToastNotification'}>
        <div>
          <div>{toast?.body}</div>
        </div>
      </ToastNotification>
    </div>
  )
}

export default ToastNotifications
